/**
 * JourneyStepper — the one consistent "where am I in the journey" strip for
 * the invite → account → profile → workspace flow (2026-08 owner directive:
 * every step shows what's happening, how far along the user is, and how long
 * it takes, in the official lowercase prest0 style).
 *
 * Rendered on the pricing/start account-question panel (step 2) and on the
 * trial onboarding page (steps 3–4) so the numbering never changes shape
 * between surfaces.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '../../styles/breakpoints';

export interface JourneyStep {
  key: string;
  label: string;
  /** Short time expectation, e.g. '~30 seconds'. Shown for the active step. */
  timeHint?: string;
}

/**
 * How long the DEDICATED WORKSTATION takes to finish its first boot.
 *
 * Deliberately distinct from the step-4 time hint below: under the
 * instant-workspace policy the workspace itself opens immediately and chat
 * works right away (that's the ~2 minutes of orientation), while the
 * workstation keeps building in the background for materially longer. Four
 * surfaces used to quote four different numbers (~2 min / 2–4 min / several
 * minutes / a few minutes) — this is the single frontend source of truth.
 * The backend's matching copy lives in
 * functions/src/prest0nSession/taskQueue.ts (buildWorkstationBootingReply).
 */
export const WORKSTATION_BOOT_ESTIMATE = 'several minutes';

/**
 * Waking an ASLEEP workstation (idle-lifecycle, 2026-08-18) is much faster
 * than first-time setup: a GCE resume/start plus service boot. Same SSOT
 * rule as WORKSTATION_BOOT_ESTIMATE — every surface quotes this constant.
 * Backend copy siblings: taskQueue.ts buildWorkstationWakingReply and
 * prest0nVm/wakeWorkspace.ts MESSAGES.
 */
export const WORKSTATION_WAKE_ESTIMATE = 'about a minute';

/** The canonical new-account journey. Keep labels lowercase.
 *  Every phase is announced here from the first screen (2026-08-04 owner
 *  directive: all expectations set, no surprises) — including the two
 *  optional guided steps, which happen IN the flow, never on another page.
 *
 *  Five steps, no more (task #53, 2026-08-12): the old sixth step, "meet
 *  your workspace", was an orientation recap of choices the user had JUST
 *  made — removed everywhere; finishing setup opens the workspace directly.
 *  Surfaces that know the user deferred an optional step pass a filtered
 *  copy of this list so the count shrinks dynamically (Onboarding.tsx). */
export const NEW_ACCOUNT_JOURNEY: JourneyStep[] = [
  { key: 'code', label: 'invite code', timeHint: '~10 seconds' },
  { key: 'account', label: 'create your account', timeHint: '~1 minute' },
  { key: 'profile', label: 'profile setup', timeHint: '~30 seconds' },
  { key: 'apps', label: 'connect your apps', timeHint: 'optional · ~30 seconds' },
  { key: 'team', label: 'invite your team', timeHint: 'optional · ~1 minute' },
];

const Strip = styled.div`
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  /* Wrap pass (2026-08-09): the strip measures ITSELF so the horizontal
     layout can collapse gracefully in narrow containers (the ~550px
     /pricing column, phone widths) — six nowrap labels used to wrap
     mid-flow leaving stretched connector stubs. */
  container-type: inline-size;

  ${media.laptop} {
    padding: 1.1rem 1.4rem;
    gap: 0.75rem;
  }
`;

const Counter = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  color: var(--brand-text-muted);
  font-weight: 600;

  ${media.laptop} {
    font-size: 0.78rem;
  }
`;

const Row = styled.div<{ $rail?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  flex-wrap: wrap;

  ${p => p.$rail && css`
    ${media.laptop} {
      flex-direction: column;
      align-items: stretch;
      gap: 0.4rem;
    }
  `}
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;

  ${media.laptop} {
    gap: 0.55rem;
  }
`;

const Dot = styled.div<{ $state: 'done' | 'active' | 'upcoming' }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  background: ${p => p.$state === 'done' ? '#27AE60' : p.$state === 'active' ? 'var(--brand-text)' : '#EDEAE4'};
  color: ${p => p.$state === 'upcoming' ? 'var(--brand-text-muted)' : '#fff'};

  ${media.laptop} {
    width: 26px;
    height: 26px;
    font-size: 0.72rem;
  }
`;

const StepText = styled.div<{ $state: 'done' | 'active' | 'upcoming'; $rail?: boolean }>`
  font-size: 0.78rem;
  font-weight: ${p => p.$state === 'active' ? 700 : 500};
  color: ${p => p.$state === 'active' ? 'var(--brand-text)' : p.$state === 'done' ? '#4B7B5D' : 'var(--brand-text-muted)'};
  text-transform: lowercase;
  white-space: nowrap;

  /* Compact collapse: when the strip is too narrow for six labels, keep the
     dots + the ACTIVE label only — position stays legible, nothing wraps. */
  @container (max-width: 620px) {
    ${p => p.$state !== 'active' && css`display: none;`}
  }

  ${media.laptop} {
    font-size: 0.9rem;
  }

  /* The desktop side-rail is vertical — every label always shows there. */
  ${p => p.$rail && css`
    ${media.laptop} {
      display: block;
    }
  `}
`;

const Connector = styled.div<{ $done: boolean; $rail?: boolean }>`
  flex: 1 1 12px;
  min-width: 12px;
  height: 2px;
  border-radius: 2px;
  margin: 9px 0.1rem 0;
  background: ${p => p.$done ? '#27AE60' : '#EDEAE4'};
  align-self: flex-start;

  /* Compact collapse: fixed-length connectors so a tight container never
     shows stretched stubs between wrapped rows. */
  @container (max-width: 620px) {
    flex: 0 0 10px;
    min-width: 10px;
  }

  ${media.laptop} {
    margin-top: 12px;
  }

  ${p => p.$rail && css`
    ${media.laptop} {
      flex: 0 0 auto;
      min-width: 0;
      width: 2px;
      height: 14px;
      margin: 0 0 0 12px;
      align-self: auto;
    }
  `}
`;

const Hint = styled.div`
  font-size: 0.74rem;
  color: var(--brand-text-muted);
  text-transform: lowercase;
  /* Progressive enhancement — avoids orphaning the last word in 300px rails. */
  text-wrap: pretty;

  ${media.laptop} {
    font-size: 0.82rem;
  }
`;

const HintTime = styled.span`
  white-space: nowrap;
`;

export interface JourneyStepperProps {
  steps?: JourneyStep[];
  /** Key of the step the user is on right now. */
  currentKey: string;
  /**
   * At ≥1024px, stack the steps vertically for a narrow side-rail placement
   * (mobile keeps the horizontal strip). CSS-only — same DOM, same numbering.
   */
  railOnDesktop?: boolean;
  /**
   * Free stage navigation (task #56): steps whose key is listed here render
   * as buttons — clicking one calls onStepClick. The owner's contract:
   * moving between stages must be easy and never leave onboarding, so the
   * strip itself is the navigator. Steps not listed stay inert (e.g. the
   * account-creation steps that live on other pages, or the current step).
   */
  navigableKeys?: string[];
  onStepClick?: (key: string) => void;
}

export const JourneyStepper: React.FC<JourneyStepperProps> = ({
  steps = NEW_ACCOUNT_JOURNEY,
  currentKey,
  railOnDesktop = false,
  navigableKeys,
  onStepClick,
}) => {
  const currentIndex = Math.max(0, steps.findIndex(s => s.key === currentKey));
  const current = steps[currentIndex];

  return (
    <Strip data-testid="journey-stepper" aria-label={`step ${currentIndex + 1} of ${steps.length}`}>
      <Counter>step {currentIndex + 1} of {steps.length}</Counter>
      <Row $rail={railOnDesktop}>
        {steps.map((step, i) => {
          const state: 'done' | 'active' | 'upcoming' =
            i < currentIndex ? 'done' : i === currentIndex ? 'active' : 'upcoming';
          return (
            <React.Fragment key={step.key}>
              {i > 0 && <Connector $done={i <= currentIndex} $rail={railOnDesktop} />}
              {navigableKeys?.includes(step.key) && onStepClick && step.key !== currentKey ? (
                <StepItem
                  as="button"
                  type="button"
                  onClick={() => onStepClick(step.key)}
                  title={`go to ${step.label}`}
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
                >
                  <Dot $state={state}>{state === 'done' ? '✓' : i + 1}</Dot>
                  <StepText $state={state} $rail={railOnDesktop} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    {step.label}
                  </StepText>
                </StepItem>
              ) : (
                <StepItem>
                  <Dot $state={state}>{state === 'done' ? '✓' : i + 1}</Dot>
                  <StepText $state={state} $rail={railOnDesktop}>{step.label}</StepText>
                </StepItem>
              )}
            </React.Fragment>
          );
        })}
      </Row>
      {current?.timeHint && (
        <Hint>{current.label} — <HintTime>{current.timeHint}</HintTime></Hint>
      )}
    </Strip>
  );
};

export default JourneyStepper;
